import { useEffect, useState } from 'react';
import { claimIdentity, confirmOrder, createCase, fetchCases, fetchOrderAttachment, fetchSession } from './api';
import { getIdentityToken } from './auth';
import type { CaseSummary, EntryPayload, OrderSection, SessionPayload } from './types';
import Workspace from './Workspace';
import ReconPanel from './ReconPanel';

function formatDateTime(value: string): string {
  return new Intl.DateTimeFormat('zh-TW', {
    timeZone: 'Asia/Taipei',
    month: 'numeric',
    day: 'numeric',
    weekday: 'short',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(new Date(value));
}

const CASE_STATE_LABELS: Record<string, string> = {
  PLANNING: '規劃中',
  RECON: '場勘中',
  PUBLISHED: '已發布',
  CLOSED: '已結案',
};

function Skeleton() {
  return (
    <main id="main" className="shell" aria-busy="true" aria-label="載入勤務命令">
      <div className="skeleton skeleton-title" />
      <div className="skeleton skeleton-card" />
      <div className="skeleton skeleton-card" />
      <p className="loading-copy">正在驗證 LINE 身分並讀取命令…</p>
    </main>
  );
}

function ErrorState({ message }: { message: string }) {
  return (
    <main id="main" className="shell state-page">
      <p className="eyebrow">無法開啟</p>
      <h1>勤務命令未載入</h1>
      <p>{message}</p>
      <button className="secondary-button" type="button" onClick={() => window.location.reload()}>
        重新嘗試
      </button>
    </main>
  );
}

function PublishedOrderItem({ item, session, token }: {
  item: OrderSection['items'][number]; session: SessionPayload; token: string;
}) {
  const operations = session.order.snapshot.operations;
  async function openAttachment(id: string) {
    const blob = await fetchOrderAttachment(token, id);
    const url = URL.createObjectURL(blob);
    window.open(url, '_blank', 'noopener,noreferrer');
    window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
  }
  return <article>
    <h3>{item.title}</h3>
    <p className="preserve-lines">{item.content}</p>
    {item.table?.length > 0 && <div className="table-scroll"><table><tbody>{item.table.map((row, rowIndex) => <tr key={rowIndex}>{row.map((cell, cellIndex) => <td key={cellIndex}>{cell}</td>)}</tr>)}</tbody></table></div>}
    {item.referenceType === 'timeline' && <div className="order-reference"><strong>勤務時間</strong><ul>{operations?.timeline.map((row) => <li key={row.id}>{formatDateTime(row.at)}　{row.title}</li>)}</ul></div>}
    {item.referenceType === 'teams' && <div className="order-reference"><strong>執行編組</strong><ul>{operations?.teams.map((team) => <li key={team.id}>{team.name}：{team.members.map((member) => member.name).join('、') || '尚無成員'}</li>)}</ul></div>}
    {item.referenceType === 'vehicles' && <div className="order-reference"><strong>車次</strong><ul>{operations?.vehicles.map((vehicle) => <li key={vehicle.id}>{vehicle.code}：{vehicle.members.length} / {vehicle.seatCount} 人</li>)}</ul></div>}
    {item.attachments?.length > 0 && <div className="published-attachments"><strong>附件</strong>{item.attachments.map((attachment) => <button className="text-button" type="button" key={attachment.id} onClick={() => void openAttachment(attachment.id)}>{attachment.fileName}</button>)}</div>}
  </article>;
}

function CasePicker({
  cases, busy, announcement, onOpen, onCreate,
}: {
  cases: CaseSummary[];
  busy: boolean;
  announcement: string;
  onOpen: (item: CaseSummary) => void;
  onCreate: (siteCode: string, title: string) => Promise<void>;
}) {
  const [siteCode, setSiteCode] = useState('A');
  const [title, setTitle] = useState('');
  const canCreate = cases.some((item) => item.role === 'CASE_ADMIN');
  return (
    <main id="main" className="shell state-page case-picker">
      <p className="section-kicker">Rich Menu 入口</p>
      <h1>我的案件</h1>
      <p>只顯示你有參與的案件；尚未發布的案件會直接進入工作區。</p>
      <div className="case-list">
        {cases.map((item) => (
          <button key={item.id} className="case-option" type="button" disabled={busy} onClick={() => onOpen(item)}>
            <span><strong>{item.title}</strong><small>{item.caseNo} · {item.site}</small></span>
            <span className="case-option-state">{item.hasPublishedOrder ? '查看命令' : item.role === 'VIEWER' ? '尚未發布' : '進入工作區'}</span>
          </button>
        ))}
        {cases.length === 0 && <p className="empty-state">目前沒有可開啟的案件，請從 LINE 群組案件卡進入並認領姓名。</p>}
      </div>
      {canCreate && (
        <form className="workspace-card create-case-form" onSubmit={(event) => {
          event.preventDefault();
          void onCreate(siteCode, title).then(() => setTitle(''));
        }}>
          <p className="section-kicker">案件管理者</p>
          <h2>建立案件</h2>
          <label>駐地
            <select value={siteCode} onChange={(event) => setSiteCode(event.target.value)}>
              <option value="A">A · 石牌</option><option value="B">B · 新屋</option>
              <option value="C">C · 中部</option><option value="D">D · 南部</option>
            </select>
          </label>
          <label>案件名稱<input value={title} maxLength={120} required onChange={(event) => setTitle(event.target.value)} /></label>
          <p className="field-help">編號會用西元年月日後兩碼與當日流水自動產生。</p>
          <button className="primary-button" type="submit" disabled={busy}>{busy ? '建立中…' : '建立並進入工作區'}</button>
        </form>
      )}
      <p className="workspace-notice" aria-live="polite">{announcement}</p>
    </main>
  );
}

export default function App() {
  const [entry, setEntry] = useState<EntryPayload | null>(null);
  const [token, setToken] = useState('');
  const [error, setError] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [announcement, setAnnouncement] = useState('');
  const [showWorkspace, setShowWorkspace] = useState(false);
  const [standaloneWorkspaceCaseId, setStandaloneWorkspaceCaseId] = useState('');
  const [cases, setCases] = useState<CaseSummary[] | null>(null);

  useEffect(() => {
    let active = true;
    async function load() {
      try {
        // LIFF initially stores deep-link parameters in `liff.state` and restores
        // them during liff.init(), so read `case` only after identity init.
        const identityToken = await getIdentityToken();
        const params = new URLSearchParams(window.location.search);
        const caseId = params.get('case') ?? '';
        if (!caseId) {
          const data = await fetchCases(identityToken);
          if (!active) return;
          setToken(identityToken);
          setCases(data.cases);
          return;
        }
        const data = await fetchSession(caseId, identityToken);
        if (!active) return;
        setToken(identityToken);
        setEntry(data);
        setShowWorkspace(Boolean(params.get('workspaceTab')));
      } catch (cause) {
        if (active) setError(cause instanceof Error ? cause.message : '發生未知錯誤。');
      }
    }
    void load();
    return () => { active = false; };
  }, []);

  async function handleConfirm() {
    if (!entry || entry.mode !== 'session' || !token || entry.confirmation.confirmed || confirming) return;
    const session = entry;
    setConfirming(true);
    setAnnouncement('');
    try {
      const result = await confirmOrder(session.case.id, session.order.id, token);
      setEntry({
        ...session,
        confirmation: {
          ...session.confirmation,
          confirmed: true,
          confirmedAt: result.confirmedAt,
          confirmedCount: session.confirmation.confirmedCount + 1,
        },
      });
      setAnnouncement('已確認收到。LINE 群組不會新增訊息。');
    } catch (cause) {
      setAnnouncement(cause instanceof Error ? cause.message : '確認失敗，請再試一次。');
    } finally {
      setConfirming(false);
    }
  }

  async function handleClaim(userId: string) {
    if (!entry || entry.mode !== 'claim' || confirming) return;
    setConfirming(true);
    setAnnouncement('');
    try {
      await claimIdentity(entry.caseId, userId, token);
      setEntry(await fetchSession(entry.caseId, token));
    } catch (cause) {
      setAnnouncement(cause instanceof Error ? cause.message : '認領失敗，請再試一次。');
    } finally {
      setConfirming(false);
    }
  }

  function handleOpenCase(item: CaseSummary) {
    if (!item.hasPublishedOrder && item.role !== 'VIEWER') {
      setStandaloneWorkspaceCaseId(item.id);
      return;
    }
    if (!item.hasPublishedOrder) {
      setAnnouncement('這個案件尚未發布勤務命令。');
      return;
    }
    const url = new URL(window.location.href);
    url.searchParams.set('case', item.id);
    window.location.assign(url.toString());
  }

  async function handleCreateCase(siteCode: string, title: string) {
    if (!token || confirming) return;
    setConfirming(true);
    setAnnouncement('');
    try {
      const result = await createCase(token, { siteCode, title });
      setStandaloneWorkspaceCaseId(result.case.id);
    } catch (cause) {
      setAnnouncement(cause instanceof Error ? cause.message : '案件建立失敗。');
    } finally {
      setConfirming(false);
    }
  }

  async function reloadCurrentSession() {
    if (!entry || entry.mode !== 'session') return;
    setEntry(await fetchSession(entry.case.id, token));
  }

  if (standaloneWorkspaceCaseId) {
    return <Workspace caseId={standaloneWorkspaceCaseId} token={token} onClose={() => {
      setStandaloneWorkspaceCaseId('');
      void fetchCases(token).then((result) => setCases(result.cases));
    }} />;
  }

  if (error) return <ErrorState message={error} />;
  if (cases) return <CasePicker cases={cases} busy={confirming} announcement={announcement} onOpen={handleOpenCase} onCreate={handleCreateCase} />;
  if (!entry) return <Skeleton />;
  if (entry.mode === 'claim') return (
    <main className="shell state-page claim-page">
      <p className="section-kicker">第一次使用</p>
      <h1>請認領你的姓名</h1>
      <p>只會顯示這個案件尚未綁定的虛構測試名單。認領後無法自行更換。</p>
      <div className="claim-list">
        {entry.claimable.map((person) => (
          <button key={person.id} className="claim-option" type="button" disabled={confirming} onClick={() => void handleClaim(person.id)}>
            <strong>{person.name}</strong><span>{[person.unit, person.position].filter(Boolean).join(' · ')}</span>
          </button>
        ))}
      </div>
      <p aria-live="polite">{announcement}</p>
    </main>
  );

  const session: SessionPayload = entry;
  if (showWorkspace && session.member.role !== 'VIEWER') {
    return <Workspace caseId={session.case.id} token={token} onClose={async () => {
      try {
        await reloadCurrentSession();
      } catch (cause) {
        setAnnouncement(cause instanceof Error ? cause.message : '最新版載入失敗，請重新整理。');
      } finally {
        setShowWorkspace(false);
      }
    }} />;
  }

  const isAdmin = session.member.role === 'CASE_ADMIN';
  const canEdit = session.member.role !== 'VIEWER';
  return (
    <>
      <header className="case-header">
        <div className="shell header-inner">
          <p className="case-number">{session.case.caseNo} · {session.case.site}</p>
          <h1>{session.case.title}</h1>
          <div className="status-row" aria-label="命令狀態">
            <span className="status-chip">已發布 {session.order.versionLabel}</span>
            <span>{formatDateTime(session.order.publishedAt)}</span>
          </div>
        </div>
      </header>

      <main id="main" className="shell content" tabIndex={-1}>
        <section className="summary-card" aria-labelledby="assignment-title">
          <p className="section-kicker">我的資訊</p>
          <h2 id="assignment-title">{session.member.name}</h2>
          <p>{[session.member.unit, session.member.position, session.member.role].filter(Boolean).join(' · ')}</p>
        </section>

        <section className="summary-card assignment-card" aria-labelledby="team-title">
          <p className="section-kicker">我的編組</p>
          <h2 id="team-title">{session.assignment?.teamName ?? '尚未發布編組'}</h2>
          {session.assignment ? <>
            {session.assignment.teamDescription && <p>{session.assignment.teamDescription}</p>}
            <dl className="fact-list">
              <div><dt>同組人員</dt><dd>{session.assignment.members.map((item) => item.name).join('、') || '尚無成員'}</dd></div>
              <div><dt>車次座位</dt><dd>{session.assignment.vehicleCode
                ? `${session.assignment.vehicleCode}${session.assignment.vehicleSeat ? ` · 座位 ${session.assignment.vehicleSeat}` : ''}`
                : '尚未安排'}</dd></div>
            </dl>
          </> : <p>管理者正式發布 Phase 2 編組後會顯示在這裡。</p>}
        </section>

        <section className="summary-card" aria-labelledby="assembly-title">
          <p className="section-kicker">案件資訊</p>
          <h2 id="assembly-title">目前進度</h2>
          <dl className="fact-list case-progress-list">
            <div><dt>案件階段</dt><dd>{CASE_STATE_LABELS[session.case.state] ?? session.case.state}</dd></div>
            <div><dt>計畫時程</dt><dd>{session.case.planningSchedule
              ? `${session.case.planningSchedule.title} · ${formatDateTime(session.case.planningSchedule.at)}`
              : '尚未安排'}</dd></div>
          </dl>
        </section>

        <ReconPanel
          caseId={session.case.id}
          memberId={session.member.id}
          items={session.reconItems}
          token={token}
          onUpdated={reloadCurrentSession}
        />

        <section className="orders" aria-labelledby="orders-title">
          <div className="section-heading">
            <div>
              <p className="section-kicker">正式發布內容</p>
              <h2 id="orders-title">五段命令</h2>
            </div>
            <span className="version-label">{session.order.versionLabel}</span>
          </div>
          {session.order.snapshot.sections.map((section, index) => (
            <details className="order-section" key={section.type} open={index < 2}>
              <summary>{section.title}</summary>
              <div className="order-body">
                {section.items.map((item) => (
                  <PublishedOrderItem key={item.id} item={item} session={session} token={token} />
                ))}
              </div>
            </details>
          ))}
        </section>

        {isAdmin && (
          <section className="admin-card" aria-labelledby="confirm-progress-title">
            <p className="section-kicker">管理者資訊</p>
            <h2 id="confirm-progress-title">
              已確認 {session.confirmation.confirmedCount} / {session.confirmation.memberCount}
            </h2>
            <p>確認綁定命令版本；每次重新發布都會歸零並重新計算。</p>
          </section>
        )}

        {canEdit && <section className="workspace-entry">
          <p className="section-kicker">編輯動線</p>
          <h2>計畫工作區</h2>
          <p>規劃、編組、場勘、命令與發布分成五個清楚的工作分頁。</p>
          <button className="secondary-button" type="button" onClick={() => setShowWorkspace(true)}>進入工作區</button>
        </section>}

        <div className="bottom-spacer" aria-hidden="true" />
      </main>

      <footer className="confirm-bar">
        <div className="shell confirm-inner">
          <p className="confirm-copy" aria-live="polite">
            {announcement || (session.confirmation.confirmed ? '你已確認這個版本。' : '確認只更新系統，不會在群組發訊息。')}
          </p>
          <button
            className="primary-button"
            type="button"
            disabled={session.confirmation.confirmed || confirming}
            onClick={handleConfirm}
          >
            {confirming ? '確認中…' : session.confirmation.confirmed ? '已確認收到' : '確認收到'}
          </button>
        </div>
      </footer>
    </>
  );
}
